/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React from 'react';
import { FlightLegInfo } from '../../../../shared/entities/flightLeg';
import FlightLegServiceAlerts from '../../../../shared/entities/flightLegServiceAlerts';
import MissionPlanDetails from '../../../../shared/entities/missionPlanDetails';
import ErrorBoundary from '../../../common/layout/ErrorBoundary';
import { MapContext } from '../../../common/map/BaseMapComponent';
import BaseMapContainer from '../../../common/map/BaseMapContainer';
import TerminalsInBeamMapControl from '../../../common/map_controls/TerminalsInBeamMapControl';
import OverlaysPanel from '../../../common/map_overlays/OverlaysPanel';
import PerformanceOutlookLegendPanel from '../../../common/map_overlays/PerformanceOutlookLegendPanel';
import TailTagOverlaysPanel from '../../../common/map_overlays/TailTagOverlaysPanel';
import MapSettingsPanel from '../../../common/map_settings/MapSettingsPanel';
import HubsContainer from '../../../fleet/Hubs/HubsContainer';
import WeatherOverlay from '../../../fleet/WeatherOverlay';
import PerformanceOutlookExportMap from './PerformanceOutlookExportMap';

export interface PerformanceOutlookExportMapContainerProps {
  mapId: string;
  topPosition: string;
  flightLeg: FlightLegInfo;
  missionPlan: MissionPlanDetails;
  userId: string;
  serviceAlerts: FlightLegServiceAlerts;
}

export const PERFORMANCE_OUTLOOK_EXPORT_MAP_DEFAULT_WIDTH = '100%';
export const PERFORMANCE_OUTLOOK_EXPORT_MAP_DEFAULT_HEIGHT = '100%';
export const PERFORMANCE_OUTLOOK_EXPORT_MAP_LEFT_POSITION = '0px';

const PerformanceOutlookExportMapContainer = (props: PerformanceOutlookExportMapContainerProps) => {
  const { mapId, topPosition, flightLeg, missionPlan, serviceAlerts, userId } = props;
  return (
    <BaseMapContainer
      id={mapId}
      width={PERFORMANCE_OUTLOOK_EXPORT_MAP_DEFAULT_WIDTH}
      height={PERFORMANCE_OUTLOOK_EXPORT_MAP_DEFAULT_HEIGHT}
      mapLabelsEnabled={true}
      mapSatelliteViewEnabled={false}
      mapPerformanceOutlookLegendPanelEnabled={true}
      left={PERFORMANCE_OUTLOOK_EXPORT_MAP_LEFT_POSITION}
      top={topPosition}
      additionalMapControls={(map: google.maps.Map, fullscreen: boolean) => (
        <TerminalsInBeamMapControl map={map} isFullScreen={true} isFullTailView={true} />
      )}
      persistZoom
      onlyShowZoomControls
    >
      <MapContext.Consumer>
        {map => (
          <div>
            <ErrorBoundary message="Unable to render the hubs">
              <HubsContainer map={map} />
            </ErrorBoundary>
            <ErrorBoundary message="Unable to render the weather overlay">
              <WeatherOverlay map={map} />
            </ErrorBoundary>
            <OverlaysPanel />
            <TailTagOverlaysPanel />
            <MapSettingsPanel />
            <PerformanceOutlookLegendPanel />
            <PerformanceOutlookExportMap
              flightLeg={flightLeg}
              missionPlan={missionPlan}
              userId={userId}
              serviceAlerts={serviceAlerts}
              map={map}
            />
          </div>
        )}
      </MapContext.Consumer>
    </BaseMapContainer>
  );
};

export default PerformanceOutlookExportMapContainer;
